/**
 * DevUserSwitcher - Development-only user account switcher
 *
 * This component provides a floating panel for quickly switching between
 * test user accounts during development. It only renders when:
 * - Running in development mode (import.meta.env.DEV)
 * - Test users have been seeded in the backend
 *
 * Usage:
 * 1. Run the backend seed script: cd web-app-backend && bun run build && bun run seed:dev
 * 2. Start the frontend dev server: bun run dev
 * 3. Use the floating panel in the bottom-right corner to switch users
 */

import AgricultureIcon from "@mui/icons-material/Agriculture";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { getDefaultRoute } from "app/routes";
import {
  finishLoadingState,
  initializeApp,
  initializeFarm,
  login,
  resetStore,
} from "services";
import { useAppStore } from "stores";

// Test user credentials - must match the backend seed script
const TEST_PASSWORD = "12345";
const TEST_DOMAIN = "test.ruumi.io";

interface TestUserConfig {
  key: string;
  email: string;
  name: string;
  description: string;
  userType: "farmer" | "corporate";
  badges: string[];
}

const TEST_USERS: TestUserConfig[] = [
  // Farm setup stages
  {
    key: "farmerNew",
    email: `farmer-new@${TEST_DOMAIN}`,
    name: "New Farmer",
    description: "No farm yet",
    userType: "farmer",
    badges: ["No Farm"],
  },
  {
    key: "farmerEmpty",
    email: `farmer-empty@${TEST_DOMAIN}`,
    name: "Empty Farm",
    description: "Farm with no fields or animals",
    userType: "farmer",
    badges: ["Empty"],
  },
  {
    key: "farmerFields",
    email: `farmer-fields@${TEST_DOMAIN}`,
    name: "Fields Only",
    description: "Has fields, no animal groups",
    userType: "farmer",
    badges: ["Fields"],
  },
  {
    key: "farmerComplete",
    email: `farmer-complete@${TEST_DOMAIN}`,
    name: "Complete Farm",
    description: "Full setup with fields, animals, events",
    userType: "farmer",
    badges: ["Complete"],
  },
  // Programme users
  {
    key: "farmerProgramme",
    email: `farmer-programme@${TEST_DOMAIN}`,
    name: "Programme Farmer",
    description: "Enrolled in programme, full farm",
    userType: "farmer",
    badges: ["Programme", "Complete"],
  },
  {
    key: "farmerActionPlan",
    email: `farmer-action-plan@${TEST_DOMAIN}`,
    name: "Action Plan",
    description: "For action plan testing",
    userType: "farmer",
    badges: ["Programme", "Complete"],
  },
  // Corporate
  {
    key: "corporate",
    email: `corporate@${TEST_DOMAIN}`,
    name: "Corporate User",
    description: "Access to all farmer data",
    userType: "corporate",
    badges: ["Corporate"],
  },
];

export default function DevUserSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState<string | null>(null);
  const navigate = useNavigate();
  const currentUser = useAppStore((state) => state.user);

  // Only render in development mode
  if (!import.meta.env.DEV) {
    return null;
  }

  const handleSwitchUser = async (user: TestUserConfig) => {
    setIsLoading(true);
    setLoadingUser(user.key);

    try {
      // Clear existing state
      window.localStorage.removeItem("jwt");
      resetStore();

      // Login as new user
      await login(user.email, TEST_PASSWORD);

      // Initialize app
      const res = await initializeApp();
      const isCorporateUser =
        res?.user?.corporations?.length && res?.user?.corporations?.length > 0;
      const farms = res?.farms;
      const userType = isCorporateUser ? "corporate" : "farmer";

      // Navigate based on user type
      if (farms && farms.length > 0 && !isCorporateUser) {
        const defaultFarm = farms[0];
        await initializeFarm(defaultFarm);
        navigate(getDefaultRoute(userType, defaultFarm.guid));
      } else if (isCorporateUser) {
        navigate(getDefaultRoute("corporate"));
      } else {
        // For "new farmer" user, go to welcome screen (same as post-signup)
        // For other users without farms, go directly to add-farm
        finishLoadingState();
        if (user.key === "farmerNew") {
          navigate("/signup/welcome");
        } else {
          navigate("/add-farm/enter-name");
        }
      }

      setIsOpen(false);
    } catch (error) {
      console.error("Failed to switch user:", error);
      alert(
        "Failed to switch user. Make sure the backend seed script has been run:\n\n" +
          "cd web-app-backend && bun run build && bun run seed:dev"
      );
    } finally {
      setIsLoading(false);
      setLoadingUser(null);
    }
  };

  const handleLogout = () => {
    window.localStorage.removeItem("jwt");
    resetStore();
    navigate("/login");
    setIsOpen(false);
  };

  const isCurrentUser = (email: string) => {
    return currentUser?.mail?.toLowerCase() === email.toLowerCase();
  };

  return (
    <>
      {/* Floating action button */}
      <Fab
        size="medium"
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          position: "fixed",
          top: 80,
          right: 16,
          zIndex: 9999,
          backgroundColor: "#7C3AED",
          "&:hover": {
            backgroundColor: "#6D28D9",
          },
        }}
      >
        <SwitchAccountIcon sx={{ color: "white" }} />
      </Fab>
      {/* Switcher panel */}
      <Collapse in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 140,
            right: 16,
            width: 320,
            maxHeight: "60vh",
            overflow: "auto",
            zIndex: 9999,
            borderRadius: 2,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              backgroundColor: "#7C3AED",
              color: "white",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SwitchAccountIcon />
              <Typography variant="subtitle1" fontWeight={600}>
                Dev User Switcher
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Current user info */}
          {currentUser && (
            <Box sx={{ p: 2, backgroundColor: "#F3F4F6" }}>
              <Typography variant="caption" color="text.secondary">
                Currently logged in as:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {currentUser.mail}
              </Typography>
            </Box>
          )}

          <Divider />

          {/* User list */}
          <List dense sx={{ py: 0 }}>
            {/* Farmers section */}
            <ListItem sx={{ backgroundColor: "#F9FAFB" }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <AgricultureIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="success.main"
                  >
                    FARMERS
                  </Typography>
                }
              />
            </ListItem>
            {TEST_USERS.filter((u) => u.userType === "farmer").map((user) => (
              <ListItemButton
                key={user.key}
                onClick={() => handleSwitchUser(user)}
                disabled={isLoading}
                selected={isCurrentUser(user.email)}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "#E8F5E9",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {loadingUser === user.key ? (
                    <CircularProgress size={20} />
                  ) : isCurrentUser(user.email) ? (
                    <CheckCircleIcon fontSize="small" color="success" />
                  ) : (
                    <PersonIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {user.name}
                      </Typography>
                      {user.badges.map((badge) => (
                        <Chip
                          key={badge}
                          label={badge}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            backgroundColor:
                              badge === "Programme"
                                ? "#E8F5E9"
                                : badge.includes("Ready")
                                ? "#FFF3E0"
                                : "#E3F2FD",
                          }}
                        />
                      ))}
                    </Box>
                  }
                  secondary={user.description}
                  slotProps={{
                    secondary: { variant: "caption" },
                  }}
                />
              </ListItemButton>
            ))}

            <Divider />

            {/* Corporate section */}
            <ListItem sx={{ backgroundColor: "#F9FAFB" }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <BusinessIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="secondary.main"
                  >
                    CORPORATE
                  </Typography>
                }
              />
            </ListItem>
            {TEST_USERS.filter((u) => u.userType === "corporate").map(
              (user) => (
                <ListItemButton
                  key={user.key}
                  onClick={() => handleSwitchUser(user)}
                  disabled={isLoading}
                  selected={isCurrentUser(user.email)}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: "#EDE7F6",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {loadingUser === user.key ? (
                      <CircularProgress size={20} />
                    ) : isCurrentUser(user.email) ? (
                      <CheckCircleIcon fontSize="small" color="secondary" />
                    ) : (
                      <BusinessIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {user.name}
                        </Typography>
                        <Chip
                          label="Corporate"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            backgroundColor: "#EDE7F6",
                          }}
                        />
                      </Box>
                    }
                    secondary={user.description}
                    slotProps={{
                      secondary: { variant: "caption" },
                    }}
                  />
                </ListItemButton>
              )
            )}
          </List>

          <Divider />

          {/* Actions */}
          <Box sx={{ p: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="small"
              onClick={handleLogout}
              disabled={isLoading || !currentUser}
            >
              Logout
            </Button>
          </Box>

          {/* Help text */}
          <Box sx={{ p: 1.5, backgroundColor: "#FEF3C7" }}>
            <Typography variant="caption" color="text.secondary">
              Password for all test users: <code>{TEST_PASSWORD}</code>
            </Typography>
          </Box>
        </Paper>
      </Collapse>
    </>
  );
}
